/**
 * Joining a repository another device already filled (card 01fee74a), for a
 * WORKSPACE: Connect on a fresh device whose own starting state differs from
 * the remote's ends in the conflict dialog. Its choices no longer become a
 * merge of two unrelated roots: like `git clone`, GitHub Desktop and VS Code,
 *
 *  - "Keep all remote" (nothing of this device's survives) → the branch IS the
 *    remote's (`git_adopt_remote`): no commit, no merge, one root;
 *  - "Keep local" on a file (or files only this device has) → ONE ordinary
 *    commit on top of the remote's, pushed.
 *
 * Only for a connect that started from NO history on this device
 * (`connectRepo` reports `fresh`): the one commit left behind is the
 * connect's own starting commit, whose kept content is in the new commit.
 * "Add a remote" to a history kept on this device never comes here — that
 * history is real and is pushed as it is (./localGitSync.js).
 */
import { invoke } from '@tauri-apps/api/core';
import { mirrorAbsDir } from '../workspaceMirror';
import { providerOf } from './providers';
import { branchOfLink, requireToken, credArgs, authorFor } from './gitAuth';

export const JOIN_KEEP_MESSAGE = "Keep this device's changes";

/**
 * Resolve the join's conflicts per file (keep local / keep remote).
 * @param {{path: string, side: 'local'|'remote'}[]} resolutions
 * @returns {Promise<{ status: 'ok'|'diverged', adopted: boolean }>}
 */
export async function resolveJoinConflicts(workspace, resolutions) {
  if (!workspace?.git) throw new Error('NOT_CONNECTED');
  const provider = providerOf(workspace);
  const token = await requireToken(provider, workspace.git);
  const dir = await mirrorAbsDir(workspace.id);
  const branch = branchOfLink(workspace.git);

  const res = await invoke('git_adopt_remote', {
    repoPath: dir,
    remote: 'origin',
    branch,
    // The caller's decision: a fresh device's starting commit may go — what
    // the choices keep of it is committed again on top of the remote's.
    discardLocal: true,
    resolutions,
    message: JOIN_KEEP_MESSAGE,
    ...(await authorFor(provider, workspace.git)),
  });
  if (res?.status !== 'committed') return { status: 'ok', adopted: true };
  const push = await invoke('git_push', { repoPath: dir, ...credArgs(provider, token, workspace.git), remote: 'origin', branch });
  return { status: push?.status === 'ok' ? 'ok' : 'diverged', adopted: false };
}
